import { HttpResponse } from "owp.http";
import { createContext } from "preact";
import type { ComponentChildren, JSX } from "preact";
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "preact/hooks";
import { Alert } from "./Alert";

type MessageType = "info" | "warning" | "error";

export interface Message {
    id: number;
    message: string;
    type: MessageType;
    // Time to live in ms
    ttl?: number;
}

export interface ErrorMessage {
    message: string;
    cause?: unknown;
}

interface HttpErrorMessage {
    message?: string;
}

type ErrorType =
    | string
    | Error
    | ErrorMessage
    | HttpErrorMessage
    | HttpResponse;

interface Props {
    children: ComponentChildren;
    initialError?: unknown;
}

interface UseMessagesContextValue {
    addInfo: (message: string) => void;
    addWarning: (message: string) => void;
    addError: (message: ErrorType) => void;
}

interface MessagesStateContextValue {
    messages: Message[];
    removeMessage: (error: Message) => void;
}

const UseMessagesContext = createContext<UseMessagesContextValue | undefined>(
    undefined,
);

const MessagesStateContext = createContext<
    MessagesStateContextValue | undefined
>(undefined);

UseMessagesContext.displayName = "UseMessagesContext";
MessagesStateContext.displayName = "MessagesStateContext";

export function MessageProvider({
    children,
    initialError,
}: Props): JSX.Element {
    const [messages, setMessages] = useState<Message[]>([]);
    const nextIdRef = useRef(1);

    useEffect(() => {
        if (initialError != null) {
            // oxlint-disable-next-line react/immutability
            addError(initialError);
        }
    }, []);

    const removeMessage = useCallback((message: Message) => {
        setMessages((prev) => prev.filter((m) => m.id !== message.id));
    }, []);

    const addMessage = useCallback(
        (message: Omit<Message, "id">) => {
            const newMessage: Message = { id: nextIdRef.current++, ...message };
            setMessages((prev) => [...prev, newMessage]);
            if (message.ttl != null) {
                // oxlint-disable-next-line react/immutability
                setTimeout(() => removeMessage(newMessage), message.ttl);
            }
        },
        [removeMessage],
    );

    const addInfo = useCallback(
        (message: string) => {
            addMessage({ message, type: "info", ttl: 10_000 });
        },
        [addMessage],
    );

    const addWarning = useCallback(
        (message: string) => {
            addMessage({ message, type: "warning" });
        },
        [addMessage],
    );

    const addError = useCallback(
        (message: unknown) => {
            void getErrorMessage(message).then((e) => {
                if (e.cause != null) {
                    console.error(e.message, e.cause);
                } else {
                    console.error(e.message);
                }
                addMessage({ message: e.message, type: "error" });
            });
        },
        [addMessage],
    );

    const useMessagesValue = useMemo<UseMessagesContextValue>(() => {
        return { addInfo, addWarning, addError };
    }, [addInfo, addWarning, addError]);

    const messagesStateValue = useMemo<MessagesStateContextValue>(() => {
        return { messages, removeMessage };
    }, [messages, removeMessage]);

    return (
        <UseMessagesContext.Provider value={useMessagesValue}>
            <MessagesStateContext.Provider value={messagesStateValue}>
                {children}
            </MessagesStateContext.Provider>
        </UseMessagesContext.Provider>
    );
}

export function Messages({
    closeText,
}: {
    closeText?: string;
}): JSX.Element | null {
    const ctx = useContext(MessagesStateContext);

    if (ctx == null) {
        throw new Error("Messages must be used within a MessageProvider");
    }

    if (ctx.messages.length === 0) {
        return null;
    }

    return (
        <div className="sticky-top mt-2">
            {ctx.messages.map((msg) => {
                return (
                    <Alert
                        key={msg.id}
                        variant={getAlertVariant(msg.type)}
                        closeText={closeText ?? "Stäng"}
                        onClose={() => ctx.removeMessage(msg)}
                    >
                        {msg.message}
                    </Alert>
                );
            })}
        </div>
    );
}

export function useMessages(): UseMessagesContextValue {
    const ctx = useContext(UseMessagesContext);
    if (ctx == null) {
        throw new Error("useMessages must be used within a MessageProvider");
    }
    return ctx;
}

function getAlertVariant(type: MessageType): "info" | "warning" | "danger" {
    switch (type) {
        case "info":
            return "info";
        case "warning":
            return "warning";
        case "error":
            return "danger";
        default: {
            const _ensureExhaustive: never = type;
            throw new Error("Unknown message type");
        }
    }
}

async function getErrorMessage(error: unknown): Promise<ErrorMessage> {
    if (typeof error === "string") {
        return { message: error };
    }

    if (error instanceof Error) {
        return { message: error.message, cause: error };
    }

    if (error instanceof HttpResponse) {
        const httpMsg = await getHttpResponseMessage(error);
        const fullMessage = `Problem på servern${httpMsg != null ? `: ${httpMsg}` : ""}`;
        return { message: fullMessage, cause: error };
    }

    if (isErrorMessage(error)) {
        return error;
    }

    return {
        message: "Okänt fel",
        cause: error,
    };
}

async function getHttpResponseMessage(
    response: HttpResponse,
): Promise<string | undefined> {
    try {
        // oxlint-disable-next-line default-case, typescript/switch-exhaustiveness-check
        switch (response.responseType) {
            case "":
            case "text":
            case "json":
                return response.json<HttpErrorMessage>().message;
            case "blob": {
                const t = await response.blob().text();
                const d: unknown = JSON.parse(t);
                return isErrorMessage(d) ? d.message : t;
            }
            case "arraybuffer":
            case "document":
                return undefined;
            default: {
                const _ensureExhaustive: never = response.responseType;
                throw new Error("Unknown response type");
            }
        }
    } catch (error: unknown) {
        console.error("Failed to get http response message", error);
        return undefined;
    }
}

function isErrorMessage(value: unknown): value is ErrorMessage {
    if (typeof value !== "object" || value == null) {
        return false;
    }
    return "message" in value && typeof value.message === "string";
}
